import React, { FC, useCallback, useMemo, useState } from 'react';
import { localized } from 'shared/state/i18n/i18n';
import { SkioldCheckbox } from '../../skiold-components/skiold-checkbox/skiold-checkbox';
import { SkioldTableGroupGrid } from '../../skiold-components/skiold-table/skiold-table-grouped-grid/Skiold-table-grouped-grid';
import {
	SkioldTableGroupGridColumnType,
	SkioldTableGroupGridPropsFromParent,
} from '../../skiold-components/skiold-table/skiold-table-grouped-grid/skiold-table-grouped-grid-helper';
import { ViewWeb } from '../../utils/web-view';
import { SentDataPanel } from './sent-data-panel';

export interface ResendEsfTableWithDetailProps extends SkioldTableGroupGridPropsFromParent {
	getDetailMessage: (row: any) => string | undefined;
}

export const ResendEsfTableWithDetail: FC<ResendEsfTableWithDetailProps> = React.memo(
	({ getDetailMessage, data, columns, ...rest }) => {
		const [shownDetails, setShownDetails] = useState<Set<string>>(new Set());

		const augmentedData = useMemo(
			() => data.map(item => ({ ...item, showDetail: shownDetails.has(item.id) })),
			[data, shownDetails],
		);

		const makeShowDetailCell = useCallback(
			(setShownDetails: React.Dispatch<React.SetStateAction<Set<string>>>) => (item: any) =>
				(
					<SkioldCheckbox
						isChecked={item.showDetail ?? false}
						onClick={() =>
							setShownDetails(prev => {
								const next = new Set(prev);
								next.has(item.id) ? next.delete(item.id) : next.add(item.id);
								return next;
							})
						}
					/>
				),
			[],
		);

		const extendedColumns = useMemo<SkioldTableGroupGridColumnType[]>(
			() => [
				...columns,
				{
					columnName: 'showDetail',
					name: 'showDetail',
					title: localized('ShowSentData'),
					getCellValue: makeShowDetailCell(setShownDetails),
					className: 'show-detail-column',
					width: 60,
					sortingDisabled: true,
					filteringDisabled: true,
				},
			],
			[columns, makeShowDetailCell],
		);

		const detailItems = useMemo(() => augmentedData.filter(d => d.showDetail), [augmentedData]);

		const handleClear = useCallback(() => setShownDetails(new Set()), []);

		return (
			<ViewWeb className="flex-row resend-esf-detail-wrapper">
				<SkioldTableGroupGrid data={augmentedData} columns={extendedColumns} {...rest} />
				{detailItems.length > 0 && (
					<SentDataPanel items={detailItems} onClear={handleClear} getDetailMessage={getDetailMessage} />
				)}
			</ViewWeb>
		);
	},
);
